import { useCallback, useEffect, useState } from 'react'
import { Loader2, Target, TriangleAlert, Info } from 'lucide-react'
import { SectionHeader } from '@/components/erp/primitives'
import { Note, Forbidden } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan } from '@/shared/auth/session'
import { useElectionContext } from '@/shared/election/context'
import {
  listVictoryPlans, getProjection, getBacktest,
  type VictoryPlan, type VictoryProjection, type VictoryBacktest,
} from './api'

/**
 * Path to Victory.
 *
 * ## The one screen that must not flatter its owner
 *
 * Every other module here reports what happened. This one projects what will, and a projection
 * is the single thing a campaign will act on without checking. So the page is built to lead
 * with its own credibility rather than its headline: when assessment coverage is below the
 * floor, the verdict banner comes FIRST and the gap is rendered muted, unlabelled as a finding
 * and impossible to read as one.
 *
 * A confident number built on two percent of the register is not a smaller version of a good
 * answer. It is a different kind of object, and showing it in the same typography as a real
 * one is how a dashboard lies without containing a wrong figure.
 */
export function VictoryPage() {
  const canRead = useCan('victory:read')
  const { electionId } = useElectionContext()

  const [plans, setPlans] = useState<VictoryPlan[] | null>(null)
  const [planId, setPlanId] = useState<string | null>(null)
  const [proj, setProj] = useState<VictoryProjection | null>(null)
  const [back, setBack] = useState<VictoryBacktest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const loadPlans = useCallback(async () => {
    if (!canRead) return
    try {
      const rows = await listVictoryPlans(electionId ?? undefined)
      setPlans(rows)
      setPlanId((current) => current ?? rows.find((p) => p.status === 'ACTIVE')?.id
        ?? rows[0]?.id ?? null)
      setError(null)
    } catch (err) {
      setError(explain(err))
      setPlans([])
    }
  }, [canRead, electionId])

  useEffect(() => { void loadPlans() }, [loadPlans])

  useEffect(() => {
    if (!planId) return
    let cancelled = false
    setBusy(true)
    getProjection(planId)
      .then((p) => { if (!cancelled) { setProj(p); setError(null) } })
      .catch((err) => { if (!cancelled) setError(explain(err)) })
      .finally(() => { if (!cancelled) setBusy(false) })
    return () => { cancelled = true }
  }, [planId])

  if (!canRead) return <Forbidden what="the path to victory" />

  const plan = plans?.find((p) => p.id === planId) ?? null

  async function runBacktest() {
    if (!planId) return
    // No election argument: the server picks the most recent completed one that is NOT this
    // plan's own. Scoring a plan against its own election is circular and would flatter it.
    try {
      setBack(await getBacktest(planId))
    } catch (err) {
      setError(explain(err))
    }
  }

  return (
    <div className="space-y-4">
      {/* With a single plan the selector is hidden, so the plan's own name is the only thing
          on screen that says which seat these numbers are about. */}
      <SectionHeader
        title="Path to Victory"
        description={plan
          ? `${plan.name} — how many votes it takes, how many we project, and where the gap is closable.`
          : 'How many votes it takes, how many we project, and where the gap is closable.'}
        right={plans && plans.length > 1 ? (
          <select className="erp-input" value={planId ?? ''}
                  onChange={(e) => { setPlanId(e.target.value); setBack(null) }}>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : undefined}
      />

      {error && <Note tone="bad">{error}</Note>}
      {plans?.length === 0 && (
        <Note tone="info">
          No victory plan yet. One plan names the seat, the rule it is won under, and the
          turnout to assume where nothing has been measured.
        </Note>
      )}

      {busy && !proj && (
        <div className="px-4 py-8 text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Computing the projection…
        </div>
      )}

      {proj && (
        <>
          {/*
            The verdict comes before the number, always. Below the coverage floor this is the
            only thing on the page that should be read as a finding.
          */}
          {!proj.decisionGrade ? (
            <div className="rounded-md border border-red-300 dark:border-red-800
                            bg-red-50 dark:bg-red-950/30 p-4">
              <div className="flex items-start gap-3">
                <TriangleAlert className="h-5 w-5 text-red-700 dark:text-red-300 shrink-0 mt-0.5" />
                <div>
                  <div className="text-erp-sm font-semibold text-red-900 dark:text-red-200">
                    Not decision-grade — {proj.assessedCoveragePct}% of the register has been
                    assessed
                  </div>
                  <p className="text-erp-xs text-red-900/80 dark:text-red-200/80 mt-1 max-w-4xl">
                    {proj.credibilityNote}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <Note tone={proj.coverageVerdict === 'THIN' ? 'warn' : 'info'}>
              {proj.credibilityNote}
            </Note>
          )}

          <div className="grid gap-3 md:grid-cols-4">
            <Stat label="Votes needed" value={proj.targetVotes}
                  hint={proj.targetIsFloor
                    ? 'A floor: an even split of turnout. Beating the leading rival may take more.'
                    : 'Under this plan’s rule.'} />
            <Stat label="Projected for us" value={proj.projectedVotes}
                  muted={!proj.decisionGrade}
                  hint={`Between ${fmt(proj.projectedVotesLow)} and ${fmt(proj.projectedVotesHigh)} — `
                        + 'the width of the definition, not a sampling interval.'} />
            <Stat label={proj.gap > 0 ? 'Short by' : 'Ahead by'}
                  value={Math.abs(proj.gap)} muted={!proj.decisionGrade}
                  tone={proj.decisionGrade ? (proj.gap > 0 ? 'bad' : 'good') : 'muted'}
                  hint={proj.decisionGrade
                    ? 'Against the target above.'
                    : 'Shown for completeness. Not a finding at this coverage.'} />
            <Stat label="Projected turnout" value={proj.projectedTurnout}
                  hint={`of ${fmt(proj.registered)} registered · `
                        + `${proj.stationsWithMeasuredTurnout} of ${proj.stationsTotal} `
                        + 'stations from a measured prior result'} />
          </div>

          {proj.sensitivity && (
            <div className="border border-border rounded-md p-4">
              <h3 className="text-erp-sm font-semibold">If we are five points out</h3>
              <p className="text-erp-xs text-muted-foreground mb-2">
                Not a confidence interval and not claimed as one. It answers the question a
                campaign manager actually has: is this robust, or balanced on a knife edge?
              </p>
              <div className="flex flex-wrap gap-6 text-erp-sm">
                <span>Worse on both: <b>{fmt(proj.sensitivity.worstCase)}</b>{' '}
                  <span className={proj.sensitivity.winsInWorstCase
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-red-700 dark:text-red-300'}>
                    {proj.sensitivity.winsInWorstCase ? 'still clears the floor' : 'below the floor'}
                  </span></span>
                <span>Better on both: <b>{fmt(proj.sensitivity.bestCase)}</b></span>
              </div>
            </div>
          )}

          <div className="border border-border rounded-md p-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-erp-sm font-semibold">How wrong was this method last time?</h3>
              {!back && (
                <button className="erp-btn" onClick={() => void runBacktest()}>
                  <Target className="h-3.5 w-3.5" /> Run the backtest
                </button>
              )}
            </div>
            {!back ? (
              <p className="text-erp-xs text-muted-foreground">
                Replays this plan’s turnout assumption against a completed election’s verified
                returns. A forecast with no error history is an opinion in a table.
              </p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-6 text-erp-sm">
                  <span>Mean error <b>{back.meanAbsoluteErrorPp} points</b> of turnout</span>
                  <span>Worst <b>{back.worstErrorPp}</b> at {back.worstStation}</span>
                  <span>{back.stationsScored} stations scored</span>
                </div>
                <p className="text-erp-xs text-muted-foreground flex gap-2">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />{back.caveat}
                </p>
              </div>
            )}
          </div>

          <div className="border border-border rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-erp-sm font-semibold">Where the gap is</h3>
              <p className="text-erp-xs text-muted-foreground">
                Worst first. This is a work queue, not a league table — the target for each ward
                is its share of the county target, in proportion to its projected turnout.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-erp-sm">
                <thead>
                  <tr className="text-erp-xs uppercase text-muted-foreground">
                    <th className="text-left px-4 py-2">Ward</th>
                    <th className="text-right px-3 py-2">Registered</th>
                    <th className="text-right px-3 py-2">Turnout</th>
                    <th className="text-right px-3 py-2">Support</th>
                    <th className="text-right px-3 py-2">Projected</th>
                    <th className="text-right px-3 py-2">Its share</th>
                    <th className="text-right px-3 py-2">Gap</th>
                    <th className="text-right px-4 py-2">Assessed</th>
                  </tr>
                </thead>
                <tbody>
                  {proj.units.map((u) => (
                    <tr key={u.unitId} className="border-t border-border">
                      <td className="px-4 py-2">
                        {u.unitName}
                        {u.overridden && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide
                                           text-amber-700 dark:text-amber-300">assumed</span>
                        )}
                        {u.turnoutMeasured && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide
                                           text-emerald-700 dark:text-emerald-300">measured</span>
                        )}
                        {u.unreadable && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide
                                           text-muted-foreground">not readable</span>
                        )}
                      </td>
                      <td className="text-right px-3 py-2 tabular-nums">{fmt(u.registered)}</td>
                      <td className="text-right px-3 py-2 tabular-nums">{u.turnoutPctUsed}%</td>
                      <td className="text-right px-3 py-2 tabular-nums">
                        {u.unreadable ? '—' : `${u.supportPctUsed}%`}
                      </td>
                      <td className="text-right px-3 py-2 tabular-nums">{fmt(u.projectedVotes)}</td>
                      <td className="text-right px-3 py-2 tabular-nums">{fmt(u.targetShare)}</td>
                      <td className={`text-right px-3 py-2 tabular-nums font-medium ${
                        u.gap > 0 ? 'text-red-700 dark:text-red-300'
                                  : 'text-emerald-700 dark:text-emerald-300'}`}>
                        {u.gap > 0 ? '+' : ''}{fmt(u.gap)}
                      </td>
                      <td className="text-right px-4 py-2 tabular-nums text-muted-foreground">
                        {u.assessedCoveragePct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {proj.unreadableStations > 0 && (
            <Note tone="warn">
              {proj.unreadableStations} stations holding {fmt(proj.unreadableRegistered)} registered
              voters have nobody assessed at all. Their turnout is counted; their votes are not
              attributed to anybody. They are a blind spot, not a loss.
            </Note>
          )}
        </>
      )}
    </div>
  )
}

function Stat({ label, value, hint, tone, muted }: {
  label: string
  value: number
  hint?: string
  tone?: 'good' | 'bad' | 'muted'
  muted?: boolean
}) {
  const colour = muted ? 'text-muted-foreground'
    : tone === 'bad' ? 'text-red-700 dark:text-red-300'
    : tone === 'good' ? 'text-emerald-700 dark:text-emerald-300'
    : ''
  return (
    <div className="border border-border rounded-md p-4">
      <div className="text-erp-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-1 ${colour}`}>{fmt(value)}</div>
      {hint && <div className="text-erp-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  )
}

const fmt = (n: number) => n.toLocaleString('en-KE')
